(ns practitest.frontend.project-general-settings.events
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]))

(def db-key :project-general-settings)

(def mixpanel-event "Project General Settings")

;; Annotation palette - colors.scss token names
(def annotation-colors
  ["pt-color-status-pending" "pt-color-status-no-run" "pt-color-status-canceled" "pt-color-status-blocked"
   "pt-color-in-progress" "pt-color-status-not-covered" "pt-color-orange" "pt-color-error"
   "pt-color-status-error" "pt-color-status-running" "pt-color-primary" "pt-color-status-na"
   "pt-color-status-not-completed" "pt-color-status-started" "pt-color-success" "pt-color-green-text"])

(def setting-names
  {:task_board_support            "Task Board"
   :time_management_support       "Time Management"
   :value_score                   "Value Score"
   :status_flow_enabled           "Test Workflow"
   :test_case_versioning_enabled  "Test Versioning"
   :enforce_test_traceability     "Enforce Test Traceability"
   :enforce_run_order             "Enforce Run Order"
   :notify_next_instance_assignee "Notify Next Tester"
   :show_fast_run                 "Fast Run"
   :add_values_on_import_api      "Add New List Values on Import / API"
   :api                           "API Tests"
   :firecracker                   "FireCracker Tests"
   :xbot                          "xBot Tests"})

(def automation-types ["api" "firecracker" "xbot"])

(defn- general-error [db]
  {:db       (assoc-in db [db-key :saving?] false)
   :dispatch [::notifications/error2
              (get-translation "generic.error.general" "Something went wrong. Please try again.")]})

(defn- form-from-data [data]
  {:name             (or (:name data) "")
   :description      (or (:description data) "")
   :settings         (:settings data)
   :annotation_types (vec (:annotation_types data))
   :automation_types (set (:automation_types data))})

(defn- json-request [method route-name params on-success on-failure]
  {:method          method
   :uri             (broutes/get-url route-name {:id (:id params)} {:format "json"})
   :params          (dissoc params :id)
   :format          (ajax/json-request-format)
   :response-format (ajax/json-response-format {:keywords? true})
   :on-success      on-success
   :on-failure      on-failure})

(re-frame/reg-event-fx
  ::fetch
  (fn [{:keys [db]} _]
    {:http-xhrio (json-request :get "projects#edit" {:id (subs/get-current-project-id db)}
                               [::fetched] [::request-failed])}))

(re-frame/reg-event-db
  ::fetched
  (fn [db [_ data]]
    (assoc db db-key {:data data
                      :form (form-from-data data)})))

(re-frame/reg-event-fx
  ::request-failed
  (fn [{:keys [db]} _]
    (general-error db)))

(re-frame/reg-event-db
  ::reset
  (fn [db _]
    (dissoc db db-key)))

(re-frame/reg-event-db
  ::set-field
  (fn [db [_ field value]]
    (-> db
        (assoc-in [db-key :form field] value)
        (update-in [db-key :errors] dissoc field))))

(re-frame/reg-event-fx
  ::set-setting
  (fn [{:keys [db]} [_ setting checked?]]
    (cond-> {:db (assoc-in db [db-key :form :settings setting] checked?)}
      ;; Existing events from the old Project tab
      (= setting :status_flow_enabled)
      (assoc ::mixpanel/track ["Approvals (Test Workflow)"
                               {"Action" (if checked? "Enable Approvals" "Disable Approvals")}])
      (= setting :test_case_versioning_enabled)
      (assoc ::mixpanel/track ["Test Versioning - project settings"
                               {"Action" (if checked? "Enable Versioning" "Disable Versioning")}]))))

(def ^:private logo-extensions #{"jpg" "jpeg" "png" "gif"})
(def ^:private logo-max-size (* 1024 1024))

(defn- logo-error [file]
  (let [extension (str/lower-case (last (str/split (.-name file) #"\.")))]
    (cond
      (not (logo-extensions extension))
      (get-translation "project-settings.general.error.logo-type" "Only JPG, PNG and GIF files are allowed")

      (> (.-size file) logo-max-size)
      (get-translation "attachments.errors.file-too-large" "File is too large"))))

(re-frame/reg-event-fx
  ::choose-logo
  (fn [{:keys [db]} [_ file]]
    (if-let [error (logo-error file)]
      {:dispatch [::notifications/error2 error]}
      {:db              (-> db
                            (assoc-in [db-key :form :logo-file] file)
                            (assoc-in [db-key :form :logo-preview] (js/URL.createObjectURL file)))
       ::mixpanel/track [mixpanel-event {"Action" "Logo Uploaded"}]})))

(defn- validate [{:keys [name]}]
  (cond-> {}
    (str/blank? name)
    (assoc :name [(get-translation "project-settings.general.error.name-required" "Project name is required")])))

(defn- form-data [{:keys [name description settings annotation_types automation_types logo-file]}]
  (let [fd (js/FormData.)]
    (.append fd "project[name]" name)
    (.append fd "project[description]" description)
    (doseq [[setting checked?] settings]
      (.append fd (str "project[" (cljs.core/name setting) "]") (if checked? "1" "0")))
    (.append fd "project[annotation_types]"
             (str/join ";" (map (fn [[color text]] (str color "|" text)) annotation_types)))
    ;; The empty value lets the server know when every type was turned off
    (doseq [automation-type (conj (vec automation_types) "")]
      (.append fd "project[automation_types][]" automation-type))
    (when logo-file
      (.append fd "project[logo]" logo-file))
    fd))

(re-frame/reg-event-fx
  ::save
  (fn [{:keys [db]} _]
    (let [form   (get-in db [db-key :form])
          errors (validate form)]
      (if (seq errors)
        {:db (assoc-in db [db-key :errors] errors)}
        {:db         (assoc-in db [db-key :saving?] true)
         :http-xhrio {:method          :put
                      :uri             (broutes/get-url "projects#update"
                                                        {:id (subs/get-current-project-id db)}
                                                        {:format "json"})
                      :body            (form-data form)
                      :response-format (ajax/json-response-format {:keywords? true})
                      :on-success      [::saved]
                      :on-failure      [::save-failed]}}))))

(defn- tracked-settings [data]
  (into (:settings data)
        (for [automation-type automation-types]
          [(keyword automation-type) (contains? (set (:automation_types data)) automation-type)])))

(defn- settings-events [before after]
  (for [[setting checked?] after
        :when (not= checked? (get before setting))]
    [mixpanel-event {"Action"  (if checked? "Setting Enabled" "Setting Disabled")
                     "setting" (get setting-names setting (name setting))}]))

(re-frame/reg-event-fx
  ::saved
  (fn [{:keys [db]} [_ data]]
    (let [before       (get-in db [db-key :data])
          annotations? (not= (:annotation_types before) (:annotation_types data))
          events       (cond-> (vec (settings-events (tracked-settings before) (tracked-settings data)))
                         annotations?
                         (conj [mixpanel-event {"Action" "Annotation Types Customize Clicked"}]))]
      {:db       (assoc db db-key {:data data
                                   :form (form-from-data data)})
       :fx       (mapv (fn [event] [::mixpanel/track event]) events)
       :dispatch [::notifications/success2
                  (get-translation "project-settings.general.notify.saved" "Project was successfully updated.")]})))

(re-frame/reg-event-fx
  ::save-failed
  (fn [{:keys [db]} [_ {:keys [status response]}]]
    (if (= status 422)
      (let [{:keys [errors error]} response]
        (cond-> {:db (-> db
                         (assoc-in [db-key :saving?] false)
                         (assoc-in [db-key :errors] errors))}
          error
          (assoc :dispatch [::notifications/error2 error])))
      (general-error db))))

(re-frame/reg-event-db
  ::set-remove-logo-confirm
  (fn [db [_ show?]]
    (assoc-in db [db-key :remove-logo-confirm?] show?)))

(re-frame/reg-event-fx
  ::remove-logo
  (fn [{:keys [db]} _]
    {:db         (assoc-in db [db-key :remove-logo-confirm?] false)
     :http-xhrio (json-request :delete "projects#remove_logo" {:id (subs/get-current-project-id db)}
                               [::logo-removed] [::request-failed])}))

(re-frame/reg-event-fx
  ::logo-removed
  (fn [{:keys [db]} [_ {:keys [notice]}]]
    {:db       (-> db
                   (assoc-in [db-key :data :logo_url] nil)
                   (update-in [db-key :form] dissoc :logo-file :logo-preview))
     :dispatch [::notifications/success2 notice]}))

(re-frame/reg-event-db
  ::open-annotation-modal
  (fn [db _]
    (assoc-in db [db-key :annotation-modal]
              {:types    (get-in db [db-key :form :annotation_types])
               :new-text ""
               :color    (first annotation-colors)})))

(re-frame/reg-event-db
  ::close-annotation-modal
  (fn [db _]
    (update db db-key dissoc :annotation-modal)))

;; Saved as the real color value, since ET, Kanban and charts use it as CSS as is
(defn- token-value [token]
  (str/trim (.getPropertyValue (js/getComputedStyle js/document.documentElement) (str "--" token))))

(defn add-annotation-type [{:keys [new-text color] :as modal}]
  (-> modal
      (update :types conj [(token-value color) (str/trim new-text)])
      (assoc :new-text "")))

(defn delete-annotation-type [modal text]
  (update modal :types (fn [types] (filterv #(not= text (second %)) types))))

(re-frame/reg-event-db
  ::update-annotation-modal
  (fn [db [_ f & args]]
    (apply update-in db [db-key :annotation-modal] f args)))

(re-frame/reg-event-db
  ::apply-annotation-types
  (fn [db _]
    (-> db
        (assoc-in [db-key :form :annotation_types] (get-in db [db-key :annotation-modal :types]))
        (update db-key dissoc :annotation-modal))))

(re-frame/reg-event-db
  ::set-automation
  (fn [db [_ automation-type enabled?]]
    (update-in db [db-key :form :automation_types] (if enabled? conj disj) automation-type)))

(re-frame/reg-event-fx
  ::contact-support-clicked
  (fn [_ _]
    {::mixpanel/track [mixpanel-event {"Action" "Contact Support Clicked"}]}))
